import { Facet, Filter, FilterModel, RangeValue, SimpleValue } from '@empathy/search-types';
import { inject, injectable } from 'inversify';
import { Store } from 'vuex';
import { DEPENDENCIES } from '../container/dependencies.const';
import { FacetConfig } from '../models';
import { Facets } from '../models';
import { GETTERS_KEYS, RootStoreState } from '../store';
import { GetFacetConfigByName } from '../store/config/getters/facet.getter';
import { Dictionary } from '../utils';
import { FacetsReconstructor } from './services.types';

@injectable()
export class FacetsReconstructorService implements FacetsReconstructor {
  constructor(
    @inject(DEPENDENCIES.Store)
    private store: Store<RootStoreState>
  ) {}

  private get facetConfig(): GetFacetConfigByName {
    return this.store.getters[GETTERS_KEYS.config.facet];
  }

  reconstruct(filtersValues: Dictionary<string[]>): Facets {
    return Object.entries(filtersValues)
      .reduce((facets, [facetName, filterValues]) => {
        filterValues.forEach(filterValue => filterValue && this.pushFilterToFacet(facets, facetName, filterValue));
        return facets;
      }, {} as Facets);
  }

  private pushFilterToFacet(facets: Facets, facetName: string, filterValue: string) {
    const facet = facets[facetName]
      ? facets[facetName]
      : this.createFacet(facets, facetName);
    const filter = this.createFilter(facet, filterValue);
    facet.filters.push(filter);
  }

  private createFacet(facets: Facets, facetName: string): Facet {
    const facetConfig = this.facetConfig(facetName);
    return facets[facetName] = {
      filters: [],
      modelName: facetName,
      title: facetName
    };
  }

  private createFilter(facet: Facet, filterValue: string): Filter {
    const facetConfig: FacetConfig = this.facetConfig(facet.modelName);
    const filterModelName = facetConfig.mapping.filterModelName;
    return {
      callbackInfo: {},
      children: [],
      count: 0,
      facet,
      id: `${ facet.modelName }:${ filterValue }`,
      modelName: filterModelName,
      parent: null,
      selected: true,
      title: filterValue,
      value: this.mapFilterValue(filterModelName, filterValue)
    };
  }

  private mapFilterValue(filterModel: string, filterValue: string): Filter['value'] {
    switch (filterModel) {
      case FilterModel.currencyRange:
      case FilterModel.range:
        const [min, max] = filterValue.split(' TO ');
        return { min: Number.parseFloat(min) || null, max: Number.parseFloat(max) || null } as RangeValue;
      case FilterModel.simple:
      default:
        return { filter: filterValue } as SimpleValue;
    }
  }
}
